import { RefreshCw, CheckCircle2, AlertTriangle, CircleDashed, ExternalLink } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const STAGE_LABELS = {
  "stage-booking-submitted": "New Inquiry",
  "stage-agreement-sent": "Agreement Sent",
  "stage-agreement-signed": "Agreement Signed",
  "stage-paid": "Rental Paid & Confirmed",
  "stage-active": "Active Rental",
  "stage-extended": "Extended",
  "stage-returned": "Returned",
  "stage-review-requested": "Review Requested",
  "stage-review-received": "Review Received",
  "stage-completed": "Completed",
  "stage-cancelled": "Cancelled",
};

const syncAgo = (ts) => {
  if (!ts) return "never";
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 10) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const money = (v) => {
  const n = Number(v || 0);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fullTime = (ts) => {
  if (!ts) return "never";
  try { return fmtDateTime(ts); } catch { return ts; }
};

const HoverRow = ({ label, value, tone = "text-white/80" }) => (
  <div className="flex items-start justify-between gap-4 text-[11px] leading-tight">
    <span className="text-white/40 shrink-0">{label}</span>
    <span className={`text-right font-medium break-all ${tone}`}>{value}</span>
  </div>
);

// Compact CRM opportunity sync chip. Shows the current GHL pipeline stage,
// opportunity value, last sync time, and success/failure at a glance.
// - Clickable → opens the GoHighLevel Opportunity in a new tab (when linkable).
// - Hover → rich details panel (pipeline, stage, opp id, value, last sync, status, error).
// - Pass onResync + resyncing to render an inline one-click retry button.
export const CrmStatusChip = ({ booking, className = "", onResync, resyncing = false, locationId = "", pipelineName = "" }) => {
  const stage = booking?.ghl_opportunity_stage;
  const synced = !!booking?.ghl_last_sync_at;
  const ok = booking?.ghl_last_sync_ok;
  const err = booking?.ghl_last_sync_error;
  const value = booking?.ghl_opportunity_value;
  const oppId = booking?.ghl_opportunity_id;
  const testId = `crm-chip-${booking?.id}`;
  const failed = synced && ok === false;
  const neverSynced = !synced && !stage;

  const oppUrl = locationId && oppId
    ? `https://app.gohighlevel.com/v2/location/${locationId}/opportunities/list/${oppId}?tab=OpportunityDetails`
    : null;

  const openOpp = (e) => {
    if (!oppUrl) return;
    e.stopPropagation();
    window.open(oppUrl, "_blank", "noopener,noreferrer");
  };

  const ResyncBtn = onResync ? (
    <button
      type="button"
      data-testid={`crm-resync-${booking?.id}`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!resyncing) onResync(booking); }}
      disabled={resyncing}
      title="Re-sync this booking to GoHighLevel"
      className="ml-0.5 inline-flex items-center justify-center rounded-full p-1 text-white/50 hover:text-[#F2B705] hover:bg-white/10 transition-colors disabled:opacity-60"
    >
      <RefreshCw size={11} className={resyncing ? "animate-spin" : ""} />
    </button>
  ) : null;

  const tone = neverSynced
    ? "border-white/10 text-white/40 bg-white/[0.03]"
    : failed
      ? "border-red-500/40 text-red-300 bg-red-500/[0.08]"
      : "border-emerald-500/30 text-emerald-300 bg-emerald-500/[0.08]";

  const label = neverSynced ? "not synced" : (STAGE_LABELS[stage] || (failed ? "Sync failed" : "Synced"));
  const Icon = neverSynced ? CircleDashed : (failed ? AlertTriangle : CheckCircle2);
  const iconTone = neverSynced ? "" : (failed ? "text-red-400" : "text-emerald-400");

  const HoverPanel = (
    <div
      data-testid={`${testId}-hover`}
      className="pointer-events-none absolute right-0 top-full mt-1.5 z-50 w-64 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150"
    >
      <div className="rounded-xl border border-white/10 bg-[#0A0A0A] shadow-2xl p-3 space-y-1.5">
        <div className="text-[11px] font-bold text-white/90 flex items-center gap-1.5 pb-1.5 border-b border-white/10">
          {oppUrl ? <ExternalLink size={11} className="text-[#F2B705]" /> : null}
          GoHighLevel Opportunity
        </div>
        <HoverRow label="Pipeline" value={pipelineName || "—"} />
        <HoverRow label="Stage" value={STAGE_LABELS[stage] || (neverSynced ? "—" : "Synced")} />
        <HoverRow label="Opportunity ID" value={oppId || "—"} tone="text-white/60" />
        <HoverRow label="Value" value={value != null ? money(value) : "—"} />
        <HoverRow label="Last sync" value={fullTime(booking?.ghl_last_sync_at)} />
        <HoverRow
          label="Sync status"
          value={neverSynced ? "Not synced" : (failed ? "Failed" : "Success")}
          tone={neverSynced ? "text-white/50" : (failed ? "text-red-300" : "text-emerald-300")}
        />
        {failed && err && <HoverRow label="Error" value={err} tone="text-red-300" />}
        {oppUrl && <div className="text-[10px] text-white/35 pt-1 border-t border-white/10">Click chip to open in GoHighLevel ↗</div>}
      </div>
    </div>
  );

  return (
    <span className={`group relative inline-flex ${className}`}>
      <span
        data-testid={testId}
        role={oppUrl ? "link" : undefined}
        tabIndex={oppUrl ? 0 : undefined}
        onClick={oppUrl ? openOpp : undefined}
        className={`inline-flex items-center gap-2 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${tone} ${oppUrl ? "cursor-pointer hover:brightness-125" : ""}`}
      >
        <Icon size={12} className={`shrink-0 ${iconTone}`} />
        <span data-testid={`${testId}-stage`}>CRM: {label}</span>
        {!neverSynced && value != null && value > 0 && (
          <span className="text-white/70" data-testid={`${testId}-value`}>• {money(value)}</span>
        )}
        {!neverSynced && (
          <span className="inline-flex items-center gap-1 text-white/45">
            <RefreshCw size={10} /> {syncAgo(booking?.ghl_last_sync_at)}
          </span>
        )}
        {oppUrl && <ExternalLink size={10} className="text-white/40" />}
        {ResyncBtn}
      </span>
      {HoverPanel}
    </span>
  );
};

export default CrmStatusChip;
